import axios from "axios";
import buildUrl from "build-url-ts";

import {
  CascadeDatesRequestPayload,
  CascadeDatesResponse,
  CreateDealRoomActionPayload,
  DealRoomAction,
  DealRoomActionsProgress,
  DealRoomFetchAssetsResponse,
  DealRoomFetchRecipientsResponse,
  DealRoomStage,
  PaginationResponse,
  SaveDealRoomActionPayload,
  UpdateDealRoomActionResponse,
} from "src/types/dealRoom";

const BASE_URL = process.env.REACT_APP_DEAL_ROOM_API_URL;
const DEALROOM_URL = `${BASE_URL}/dealrooms/3.0`;

export const dealRoomMapApi = {
  fetchRecipientsForMap: async (
    dealRoomId: string,
    page: number
  ): Promise<DealRoomFetchRecipientsResponse> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/people`, {
      queryParams: {
        type: "ALL",
        page,
        limit: 20,
      },
    });
    const response = await axios.get(url);

    return response.data;
  },
  
  fetchAssetsForMap: async (
    dealRoomId: string,
    page: number
  ): Promise<DealRoomFetchAssetsResponse> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/assets`, {
      queryParams: {
        assetCategory: "ALL_INTERNAL",
        page,
        limit: 20,
      },
    });
    const response = await axios.get(url);

    return response.data;
  },

  createStage: async (
    dealRoomId: string,
    payload: {
      name: string,
    },
  ): Promise<DealRoomStage> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/action-plan/stage`);
    const response = await axios.post(url, payload);

    return response.data;
  },

  reorderStage: async (
    dealRoomId: string,
    updatedStageOrder: { stageIds: string[] },
  ): Promise<DealRoomStage[]> => {
    const url = buildUrl(
      `${DEALROOM_URL}/${dealRoomId}/action-plan/stages/order`,
    );
    const response = await axios.put(url, updatedStageOrder);

    return response.data;
  },

  fetchStages: async (dealRoomId: string): Promise<DealRoomStage[]> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/action-plan/stages`);
    const response = await axios.get(url);
    if (response.status === 204) {
      return [];
    }

    return response.data;
  },

  updateStage: async (
    dealRoomId: string,
    payload: {
      id: string,
      name: string,
    },
  ): Promise<DealRoomStage> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/action-plan/stage`);
    const response = await axios.put(url, payload);

    return response.data;
  },

  deleteStage: async (
    dealRoomId: string,
    stageId: string,
  ): Promise<boolean> => {
    const url = buildUrl(
      `${DEALROOM_URL}/${dealRoomId}/action-plan/stage/${stageId}`,
    );
    const response = await axios.delete(url);

    if (response.status === 204) {
      return true;
    }

    return false;
  },

  fetchActionsForStage: async (
    dealRoomId: string,
    stageId: string,
    page: number,
  ): Promise<{
    actions: DealRoomAction[];
    pagination: PaginationResponse;
  }> => {
    const url = buildUrl(
      `${DEALROOM_URL}/${dealRoomId}/action-plan/stage/${stageId}/actions`,
      {
        queryParams: {
          page,
          limit: 20,
        },
      },
    );

    const response = await axios.get(url);

    return response.data;
  },

  createAction: async (
    dealRoomId: string,
    action: CreateDealRoomActionPayload,
  ): Promise<UpdateDealRoomActionResponse> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/action-plan/action`);
    const response = await axios.post(url, action);

    return response.data;
  },

  saveAction: async (
    dealRoomId: string,
    action: SaveDealRoomActionPayload,
  ): Promise<UpdateDealRoomActionResponse> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/action-plan/action`);
    const response = await axios.put(url, action);

    return response.data;
  },

  reorderActions: async (
    dealRoomId: string,
    updatedActionsOrder: { actionIds: string[]; stage: string },
  ): Promise<DealRoomStage[]> => {
    const url = buildUrl(
      `${DEALROOM_URL}/${dealRoomId}/action-plan/actions/order`,
    );
    const response = await axios.put(url, updatedActionsOrder);

    return response.data;
  },

  completeAction: async (
    dealRoomId: string,
    actionId: string,
    completed: boolean,
  ): Promise<UpdateDealRoomActionResponse> => {
    const url = buildUrl(
      `${DEALROOM_URL}/${dealRoomId}/action-plan/action/${actionId}/complete`,
    );
    const response = await axios.put(url, {
      completed,
    });

    return response.data;
  },

  fetchActionsProgress: async (
    dealRoomId: string,
  ): Promise<DealRoomActionsProgress> => {
    const url = buildUrl(
      `${DEALROOM_URL}/${dealRoomId}/action-plan/actions/progress`,
    );
    const response = await axios.get(url);

    return response.data;
  },

  cascadeDates: async (
    dealRoomId: string,
    payload: CascadeDatesRequestPayload,
  ): Promise<CascadeDatesResponse> => {
    const url = buildUrl(`${DEALROOM_URL}/${dealRoomId}/shift-due-dates`);
    const response = await axios.post(url, payload);

    return response.data;
  },
};
